import React, { useEffect } from 'react';
import Shuttle, { PaneState, ShuttleState } from 'components/shuttle/Shuttle';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import { Recording } from 'types/rest/assetCatalogTypes';
import SimpleTagList from 'components/ui/tags/SimpleTagList';
import BzmIcon from 'components/icons/BzmIcon';
import { useIsMount } from 'hooks/useIsMountHook';
import { MockService } from 'types/rest/mockServiceTypes';
import AssignedRecordingDetail from './AssignedRecordingDetail';
import RecordingEditor from '../../../../../../AssetCatalog/Recordings/RecordingEditor';
import { Col, Row } from 'react-bootstrap';

interface RecordingsShuttleProps {
  disabled: boolean;
  mockService: MockService;
  shuttleState: ShuttleState;
  fetchAvailableRecordings: () => void;
  fetchAssignedRecordings: () => void;
  updateRecordingShuttlePane: (panePosition: PanePosition, state: PaneState) => void;
  moveRecordings: (position: PanePosition, recordingIds: number[]) => void;
}

type PanePosition = 'LEFT' | 'RIGHT';

const RecordingsShuttle = ({
  disabled,
  mockService,
  shuttleState,
  fetchAvailableRecordings,
  fetchAssignedRecordings,
  updateRecordingShuttlePane,
  moveRecordings,
}: RecordingsShuttleProps) => {
  const leftPaneState = shuttleState.leftPane;
  const rightPaneState = shuttleState.rightPane;
  const isMovingRight = shuttleState.isMoveRightInProgress;
  const isMovingLeft = shuttleState.isMoveLeftInProgress;

  const isMount = useIsMount();

  useEffect(() => {
    if (isMount) {
      fetchAvailableRecordings();
      fetchAssignedRecordings();
    }
  }, []);

  const expandDetail = (recording: Recording, panePosition: PanePosition) => {
    const paneState = panePosition === 'LEFT' ? leftPaneState : rightPaneState;
    const updatedPaneState: PaneState = {
      ...paneState,
      expanded: paneState.expanded === recording.id ? null : recording.id,
    };

    updateRecordingShuttlePane(panePosition, updatedPaneState);
  };

  const getRecordingDetail = (recording: Recording, panePosition: PanePosition) => {
    const colSize = 3;
    return (
      <>
        {/* Recording Header */}
        <div className={'pt-5 px-5'}>
          <Row>
            <Col md={colSize}>
              <label>Name:</label>
            </Col>
            <Col>{recording.name}</Col>
          </Row>

          <Row>
            <Col md={colSize}>
              <label>Service:</label>
            </Col>
            <Col>{recording.serviceName}</Col>
          </Row>

          <Row>
            <Col md={colSize}>
              <label>Description:</label>
            </Col>
            <Col>{recording.description}</Col>
          </Row>

          <Row>
            <Col md={colSize}>
              <label>Tags:</label>
            </Col>
            <Col>{recording.tags ? <SimpleTagList tags={recording.tags} /> : '-'}</Col>
          </Row>
        </div>

        <div>
          {panePosition === 'RIGHT' && <AssignedRecordingDetail mockService={mockService} recording={recording} />}
          <RecordingEditor isCreate={false} recording={recording} isReadOnly={true} isCompact={true} />
        </div>

        {/* ./ Recording Detail*/}
      </>
    );
  };

  const getColumnDef = (panePosition: PanePosition): BlazeTableColDef[] => [
    {
      header: 'Id',
      accessor: 'id',
      isExpandLink: false,
      width: 80,
    },
    {
      header: 'Name',
      accessor: 'name',
      isExpandLink: true,
      cellComponent: (recording: Recording) => {
        return (
          <>
            <BzmIcon variant={'icon-bzm-ct-recording'} className={'mr-2'} />
            <a
              href={'#'}
              onClick={() => {
                expandDetail(recording, panePosition);
              }}
            >
              {recording.name}
            </a>
          </>
        );
      },
      width: 220,
    },
    {
      header: 'Tags',
      cellComponent: (recording: Recording) => <SimpleTagList tags={recording.tags} />,
      accessor: 'tags',
      isSortable: false,
      width: 160,
    },
    {
      header: 'Actions',
      cellComponent: (item: Recording) => {
        return (
          <div className={'flex-table-actions text-center'}>
            <button
              disabled={disabled}
              title={panePosition === 'LEFT' ? 'Assign Recording' : 'De-assign Recording'}
              onClick={() => {
                //Direction is opposite of the pane position
                const direction = panePosition === 'LEFT' ? 'RIGHT' : 'LEFT';
                moveRecordings(direction, [item.id]);
              }}
              className="btn btn-link-dark btn-no-hover"
            >
              <BzmIcon
                variant={panePosition === 'RIGHT' ? 'icon-bzm-single-assign-left' : 'icon-bzm-single-assign-right'}
                className={'icon-xs'}
              />
            </button>
          </div>
        );
      },
      accessor: 'none1',
      width: 80,
      isSortable: false,
    },
  ];

  const getTableOptions = (panePosition: 'LEFT' | 'RIGHT'): BlazeTableOptions => {
    const paneState = panePosition === 'LEFT' ? leftPaneState : rightPaneState;
    return {
      showRowExpander: true,
      columnDef: getColumnDef(panePosition),
      expandDetailComponent: (recording: Recording) => getRecordingDetail(recording, panePosition),
      selectable: true,
      sortable: true,
      pageable: true,
      pageSize: 10,
      disabled: disabled,
      paginationOverride: {
        activePage: paneState.activePage,
        pageSize: paneState.pageSize,
        total: paneState.totalResults,
      },
      sortingOverride: paneState.sort,
      noResultsText: panePosition === 'LEFT' ? 'No Recordings available' : 'No Recordings assigned',
      selectAllDropdown: false,
      selectAllType: paneState.selectAll,
    };
  };

  /**
   * Determines if the button should be disabled based on the selected items and the disabled state.
   * @param {('LEFT' | 'RIGHT')} buttonDirection - The direction of the button.
   * @returns {boolean} - True if the button should be disabled, false otherwise.
   */
  const isBtnDisabled = (buttonDirection: 'LEFT' | 'RIGHT'): boolean => {
    const selected =
      buttonDirection === 'RIGHT' ? leftPaneState.selected.length === 0 : rightPaneState.selected.length === 0;
    return disabled || selected || isMovingLeft || isMovingRight;
  };

  const updateStateHandler = async (direction: 'LEFT' | 'RIGHT', state: PaneState, reload?: boolean) => {
    updateRecordingShuttlePane(direction, state);

    if (reload) {
      if (direction === 'LEFT') {
        fetchAvailableRecordings();
      } else {
        fetchAssignedRecordings();
      }
    }
  };

  const moveRecordingsHandler = (direction: PanePosition) => {
    const selectedRecordings = direction === 'RIGHT' ? leftPaneState.selected : rightPaneState.selected;
    moveRecordings(direction, selectedRecordings);
  };

  return (
    <Shuttle
      leftButton={{ inProgress: isMovingRight, disabled: isBtnDisabled('LEFT') }}
      rightButton={{ inProgress: isMovingLeft, disabled: isBtnDisabled('RIGHT') }}
      paneConfigs={{
        left: {
          state: leftPaneState,
          title: 'Recordings in Your Catalog',
          tableOptions: getTableOptions('LEFT'),
        },
        right: {
          state: rightPaneState,
          title: 'Recordings in This <strong>Virtual Service</strong>',
          tableOptions: getTableOptions('RIGHT'),
        },
      }}
      onStateChange={updateStateHandler}
      onMove={direction => {
        moveRecordingsHandler(direction);
      }}
    />
  );
};

export default RecordingsShuttle;
